import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { EmptyState, SeverityBadge } from "@/components/shared";
import { Wrench } from "lucide-react";
import { toast } from "sonner";

export default function TechnicalSEO() {
  const { activeWebsite } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState("all");
  const [page, setPage] = useState(1);

  const load = async () => {
    if (!activeWebsite) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (severity !== "all") params.severity = severity;
      const { data: d } = await api.get(`/websites/${activeWebsite.id}/issues`, { params });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [activeWebsite?.id, severity, page]); // eslint-disable-line

  const counts = data?.severity_counts || {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Technical SEO</h1>
          <p className="text-xs text-muted-foreground">Evidence from the crawler - nothing invented. Re-run the audit after fixes.</p>
        </div>
        <Select value={severity} onValueChange={(v) => { setSeverity(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="technical-severity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            {["critical", "high", "medium", "low", "info"].map((s) => (
              <SelectItem key={s} value={s}>{s} ({counts[s] || 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2">
        {["critical", "high", "medium", "low", "info"].map((s) => (
          <button key={s} onClick={() => { setSeverity(s === severity ? "all" : s); setPage(1); }}
            className={`rounded-md border px-3 py-1.5 text-xs font-medium ${severity === s ? "border-primary bg-primary/5" : "bg-card"}`}
            data-testid={`technical-severity-chip-${s}`}>
            <span className="capitalize">{s}</span> <span className="ml-1 tabular-nums text-muted-foreground">{counts[s] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? <Skeleton className="h-64 w-full" /> : !data || data.total === 0 ? (
        <EmptyState icon={Wrench} title="No issues in this view" message="Run a website audit from the Overview page, or change the severity filter." testId="technical-empty" />
      ) : (
        <Card className="shadow-none">
          <CardContent className="scc-table-wrap p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Severity</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead className="hidden md:table-cell">URL</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((i) => (
                  <TableRow key={i.id} data-testid="technical-issue-row">
                    <TableCell><SeverityBadge severity={i.severity} /></TableCell>
                    <TableCell>
                      <div className="text-xs font-medium">{i.message}</div>
                      <div className="text-[10px] text-muted-foreground">{i.type.replaceAll("_", " ")}</div>
                    </TableCell>
                    <TableCell className="hidden max-w-[320px] truncate font-mono text-[11px] text-muted-foreground md:table-cell">{i.url}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {data && data.total > 50 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page * 50 >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
